import React from 'react';
import PropTypes from 'prop-types';
import A11yFocus from '@ids/a11yfocus';
import { styleOverrideReason } from '@ids/core';
import { themes, getTheme, ThemeContext, isValidTheme } from '@ids/theme';
import {
  Overlay,
  Wrapper,
  destructiveColor,
  specialColor,
  colorWhite,
  secondaryGhostDarkColor
} from './StyledButton';
import Label from './StyledButtonLabel';

const { bool, func, object, string, oneOf, node } = PropTypes;

/**
 * A themable Button component
 */
class Button extends React.PureComponent {
  static contextType = ThemeContext;
  static propTypes = {
    /**
     * Test  automation ID.
     */
    automationId: string,
    /**
     * The Button label
     */
    children: node,
    /**
     * Disable the Button.
     */
    // eslint-disable-next-line react/boolean-prop-naming
    disabled: bool,
    /**
     * Specifies if the button is loading
     */
    // eslint-disable-next-line react/boolean-prop-naming
    loading: bool,
    /**
     * Specifies if component when the button is in processing state
     */
    // eslint-disable-next-line react/forbid-prop-types
    loadingComponent: object,
    /**
     *  The Button Icon
     */
    // eslint-disable-next-line react/forbid-prop-types
    icon: object,
    /**
     *  The Button Icon placement, 'right', 'left' of label
     */
    iconPosition: string,
    /**
     * Button background theme, 'inverse' for dark
     */
    // eslint-disable-next-line react/boolean-prop-naming
    inverse: bool,
    /**
     * Callback function that is fired when a Button has been blurred.
     */
    onBlur: func,
    /**
     * Callback function that is fired when a Button has been clicked.
     */
    onClick: func,
    /**
     * Callback function that is fired when a Button has been focused.
     */
    onFocus: func,
    /**
     * Callback function that is fired when a Button has been clicked.
     */
    onMouseDown: func,
    /**
     * Specifies Button size, 'mini', 'standard', 'responsive' or 'jumbo'
     */
    size: oneOf(['mini', 'standard', 'jumbo', 'responsive']),
    /**
     * The application's ThemeProvider determines the default theme the component
     * will use. Use theme prop to override (e.g. 'mint', 'proconnect', 'quickbooks',
     * 'turbotax').
     */
    theme: oneOf(themes),
    /**
     * Specifies Button type, 'primary', 'secondary', 'tertiary', 'special', 'destructive', 'destructiveTertiary', 'icon', 'secondaryGhost'
     */
    buttonType: oneOf([
      'primary',
      'secondary',
      'tertiary',
      'special',
      'destructive',
      'destructiveTertiary',
      'icon',
      'secondaryGhost'
    ]),
    /**
     * The aria-label property surfaces a label to assistive technology. Use it to replace the text node for accessibility.
     */
    'aria-label': string,
    /**
     * for internal use only: set when button is focused
     */
    // eslint-disable-next-line react/boolean-prop-naming
    a11yFocus: bool,
    /**
     * Prop to accept function to assign ref on button
     */
    assignButtonRef: func,
    /**
     * When `style` or `className` prop is provided, provide the reason behind the override.
     */
    styleOverrideReason
  };

  static defaultProps = {
    automationId: undefined,
    a11yFocus: false,
    inverse: false,
    buttonType: 'primary',
    children: undefined,
    disabled: false,
    loading: false,
    loadingComponent: undefined,
    icon: undefined,
    iconPosition: 'left',
    onBlur() {},
    onClick() {},
    onFocus() {},
    onMouseDown() {},
    assignButtonRef() {},
    size: 'standard',
    theme: undefined,
    styleOverrideReason: undefined,
    'aria-label': undefined
  };

  getButtonRef = c => {
    this.props.assignButtonRef(c);
  };

  /**
   * Cloning icon prop to pass props down to svg. Applicable to button with label and icon
   */
  cloneIconLabel() {
    const { icon, iconPosition } = this.props;
    const svgProps = {
      width: '20px',
      height: '20px',
      style: {
        verticalAlign: 'middle'
      }
    };
    // eslint-disable-next-line no-unused-expressions
    iconPosition === 'left'
      ? Object.assign(svgProps, { margin: '0 4px 0 0' })
      : Object.assign(svgProps, { margin: '0 0 0 4px' });
    const element = React.cloneElement(icon, svgProps);
    return element;
  }

  /**
   * Method to clone button loading component
   */
  cloneLoadingComponent() {
    const { loadingComponent, buttonType, inverse } = this.props;
    let fillColor = '';
    if (inverse) {
      fillColor = colorWhite;
    }

    if (buttonType === 'destructive' || buttonType === 'destructiveTertiary') {
      fillColor = destructiveColor;
    }

    if (buttonType === 'special') {
      fillColor = specialColor;
    }

    if (buttonType === 'secondary') {
      fillColor = secondaryGhostDarkColor;
    }

    const element = React.cloneElement(loadingComponent, { fill: fillColor });
    return element;
  }

  /**
   * Cloning icon prop to pass props down to svg. Applicable to icon buttonType
   */
  renderIconOnly() {
    const { icon, theme } = this.props;

    const svgProps = {
      width: '20px',
      height: '20px',
      margin: '8px 0 0 0'
    };

    if (theme === 'quickbooks') {
      svgProps.width = '24px';
      svgProps.height = '24px';
    }

    const element = React.cloneElement(icon, svgProps);
    return element;
  }

  /**
   * Method to render button children: icon and/or label
   */
  renderButtonIconLabel() {
    const {
      children,
      buttonType,
      icon,
      iconPosition,
      loading,
      loadingComponent,
      size,
      inverse
    } = this.props;

    const { currentTheme } = getTheme(this.props, this.context);

    if (loadingComponent && loading) {
      return <span>{loadingComponent && this.cloneLoadingComponent()}</span>;
    }

    return (
      <Label
        theme={currentTheme}
        buttonType={buttonType}
        size={size}
        inverse={inverse}
        icon={icon}
      >
        {icon && iconPosition === 'left' && this.cloneIconLabel()}
        {children}
        {icon && iconPosition === 'right' && this.cloneIconLabel()}
      </Label>
    );
  }

  render() {
    const {
      automationId,
      buttonType,
      children,
      icon,
      loading,
      size,
      theme,
      ...restProps
    } = this.props;

    const colorScheme = this.props.inverse ? 'dark' : 'light';
    const { currentTheme, currentColorScheme } = getTheme(
      { theme, colorScheme },
      this.context
    );

    if (!isValidTheme(currentTheme)) {
      return null;
    }

    if (!children && !restProps['aria-label']) {
      console.error('Please provide one of aria-label or children for button.');
    }

    return (
      // A11yFocus helps with styling when button is focused
      <A11yFocus>
        <Wrapper
          ref={this.getButtonRef}
          buttonType={buttonType}
          icon={icon}
          theme={currentTheme}
          data-automation-id={automationId}
          size={size}
          colorScheme={currentColorScheme}
          loading={loading ? 'true' : undefined}
          {...restProps}
        >
          <Overlay buttonType={buttonType} theme={currentTheme} size={size}>
            {children && this.renderButtonIconLabel()}
            {!children && icon && this.renderIconOnly()}
          </Overlay>
        </Wrapper>
      </A11yFocus>
    );
  }
}

Button.displayName = 'Button';
export default Button;
